import type { SessionResponse, User } from '@chatapp-inbox/shared';
import { useMutation, useQuery, useQueryClient, type QueryClient } from '@tanstack/react-query';
import {
  createUser,
  isApiError,
  listUsers,
  updateUser,
  type NewUserInput,
  type UserPatch,
} from '../../api';
import { SESSION_KEY } from '../../auth';
import { invalidateConversationLists, usersKey } from '../conversation';

/** Todos os usuários (mesmo cache que o seletor de responsável usa, para os dois sempre concordarem). */
export function useUsers() {
  return useQuery({ queryKey: usersKey, queryFn: ({ signal }) => listUsers(signal) });
}

/**
 * `gcTime: 0` nas mutações com senha: o React Query guarda as VARIÁVEIS de cada mutação no cache
 * (por padrão, 5 minutos). Assim a senha não fica em memória depois de a mutação acabar e a tela
 * sair dela. (O cache de mutações também é esvaziado ao sair da conta.)
 */
export function useCreateUser() {
  const queryClient = useQueryClient();
  return useMutation({
    gcTime: 0,
    mutationFn: (input: NewUserInput) => createUser(input),
    onSuccess: (user) => {
      const cached = queryClient.getQueryData<User[]>(usersKey);
      // Sem lista em cache não há onde acrescentar: uma lista com um só usuário seria mentira.
      if (cached) queryClient.setQueryData<User[]>(usersKey, [...cached, user]);
      else void queryClient.invalidateQueries({ queryKey: usersKey });
    },
  });
}

interface UpdateVariables {
  id: string;
  patch: UserPatch;
}

/**
 * Altera um usuário (nome, papel, situação ou senha). Depois: a resposta do servidor substitui o
 * usuário no cache (sem refazer a lista toda); se a SITUAÇÃO ou o PAPEL mudou, as listas de
 * conversas e as conversas abertas ficam obsoletas (desativar devolve as conversas da pessoa à
 * fila das não atribuídas); e, se foi a própria pessoa, a sessão passa a mostrar o nome novo.
 */
export function useUpdateUser(viewerId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    gcTime: 0,
    mutationFn: ({ id, patch }: UpdateVariables) => updateUser(id, patch),
    onSuccess: (user, { patch }) => {
      applyUpdatedUser(queryClient, user);
      if (user.id !== viewerId) return;
      patchSessionUser(queryClient, user);
      // Trocar a PRÓPRIA senha encerra as sessões da pessoa no servidor, inclusive esta. Pedir a
      // lista de novo faz o 401 chegar já, pelo caminho normal de "sessão expirada": sem isto a
      // tela ficaria de pé, parecendo viva, até o próximo pedido por acaso.
      if (patch.password !== undefined) void queryClient.invalidateQueries({ queryKey: usersKey });
    },
    // 404: o usuário deixou de existir (ou nunca existiu para este cache): a lista está velha.
    onError: (error) => {
      if (isApiError(error) && error.status === 404) {
        void queryClient.invalidateQueries({ queryKey: usersKey });
      }
    },
  });
}

function applyUpdatedUser(queryClient: QueryClient, user: User): void {
  const cached = queryClient.getQueryData<User[]>(usersKey);
  const before = cached?.find((candidate) => candidate.id === user.id);
  if (cached && before) {
    queryClient.setQueryData<User[]>(
      usersKey,
      cached.map((candidate) => (candidate.id === user.id ? user : candidate)),
    );
  } else {
    void queryClient.invalidateQueries({ queryKey: usersKey });
  }
  if (!before || before.active !== user.active || before.role !== user.role) {
    void invalidateConversationLists(queryClient);
    void queryClient.invalidateQueries({ queryKey: ['conversation'] });
  }
}

function patchSessionUser(queryClient: QueryClient, user: User): void {
  queryClient.setQueryData<SessionResponse | null | undefined>(SESSION_KEY, (session) =>
    session && session.user.id === user.id ? { ...session, user } : session,
  );
}
